import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { departmentIdScopeKey, getFirestorePathDepartmentId, normalizeDepartmentId } from '../utils/normalizeIds';

export interface EthicSurveyMeta {
  uniqueRespondentCount?: number;
  totalRecordCount?: number;
  lastSyncAt?: { toDate: () => Date } | null;
}

export async function fetchEthicSurveyMeta(): Promise<EthicSurveyMeta | null> {
  const metaSnap = await getDoc(doc(db, 'ethicSurveyMeta', 'summary'));
  if (!metaSnap.exists()) return null;
  return metaSnap.data() as EthicSurveyMeta;
}

export function formatEthicResponseCount(
  totalResponses: number,
  meta: EthicSurveyMeta | null
): string {
  if (meta?.uniqueRespondentCount != null && meta.uniqueRespondentCount !== totalResponses) {
    return `${totalResponses} (จาก ${meta.uniqueRespondentCount} ผู้ตอบแบบสอบถาม)`;
  }
  return String(totalResponses);
}
import { ETHIC_DOMAINS } from '../lib/ethicDomains';
import { THRESHOLD_ACCEPTABLE } from '../lib/ethicThresholds';

export interface EthicSurveyRow {
  id?: string;
  timestamp: string;
  position: string;
  workExperience: string;
  departmentId: string;
  departmentNameRaw?: string;
  answers: Record<number, number> | number[];
  score?: number;
}

export interface EthicFetchOptions {
  departmentIds?: string[];
  startDate?: Date;
  endDate?: Date;
  position?: string;
  workExperience?: string;
}

/** Cap full-collection reads for hospital-wide ethic dashboards (Phase 4). */
const ETHIC_WILDCARD_READ_LIMIT = 5000;

const sanitizeDepartmentIds = (departmentIds?: string[]) =>
  Array.from(
    new Set(
      (departmentIds ?? [])
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter(Boolean),
    ),
  );

const buildDepartmentIdQueryCandidates = (departmentId: string): string[] => {
  const trimmed = departmentId.trim();
  if (!trimmed) return [];
  const underscoreVariant = trimmed.replace(/-/g, '_');
  const hyphenVariant = trimmed.replace(/_/g, '-');
  const normalizedVariant = normalizeDepartmentId(trimmed);
  const firestorePathVariant = getFirestorePathDepartmentId(trimmed);
  return Array.from(
    new Set(
      [trimmed, underscoreVariant, hyphenVariant, normalizedVariant, firestorePathVariant]
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
};

const isPermissionDeniedError = (error: unknown) => {
  const code = (error as { code?: string } | null)?.code ?? '';
  const message =
    (error as { message?: string } | null)?.message?.toLowerCase?.() ?? '';
  return (
    code === 'permission-denied' ||
    message.includes('permission') ||
    message.includes('insufficient')
  );
};

function parseAnswers(doc: Record<string, unknown>): Record<number, number> {
  const out: Record<number, number> = {};
  for (let q = 1; q <= 52; q++) {
    const keys = [`${q}`, `question_${q}`, `ข้อ${q}`];
    for (const k of keys) {
      const v = doc[k];
      if (typeof v === 'number' && v >= 1 && v <= 5) {
        out[q] = v;
        break;
      }
      if (typeof v === 'string') {
        const n = Number(v.trim());
        if (n >= 1 && n <= 5) {
          out[q] = n;
          break;
        }
      }
    }
  }
  return out;
}

function computeOverallScore(answers: Record<number, number>): number {
  const vals = Object.values(answers).filter((v) => v >= 1 && v <= 5);
  if (!vals.length) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Normalize a Firestore ethicSurveys document to a dashboard row (shared with callable governance fetch). */
export function ethicSurveyRecordToRow(id: string, d: Record<string, unknown>): EthicSurveyRow {
  const departmentId = (d.departmentId as string) ?? '';
  const answers = (d.answers as Record<number, number>) ?? parseAnswers(d);
  return {
    id,
    timestamp: (d.timestamp as string) ?? '',
    position: (d.position as string) ?? '',
    workExperience: (d.workExperience as string) ?? '',
    departmentId,
    departmentNameRaw: d.departmentNameRaw as string | undefined,
    answers,
    score: typeof d.score === 'number' ? d.score : computeOverallScore(answers),
  };
}

export async function fetchEthicData(options: EthicFetchOptions = {}): Promise<EthicSurveyRow[]> {
  const coll = collection(db, 'ethicSurveys');
  const scopedDepartmentIds = sanitizeDepartmentIds(options.departmentIds);
  const isWildcard = scopedDepartmentIds.includes('*');
  const rows: EthicSurveyRow[] = [];
  const seenDocIds = new Set<string>();
  const scopedIdSet = new Set(
    scopedDepartmentIds
      .filter((id) => id !== '*')
      .map((id) => departmentIdScopeKey(id)),
  );

  const appendRows = (snapshot: Awaited<ReturnType<typeof getDocs>>) => {
    snapshot.docs.forEach((docSnap) => {
      if (seenDocIds.has(docSnap.id)) return;
      const d = docSnap.data();
      const departmentId = (d.departmentId as string) ?? '';
      const normalizedDeptId = departmentIdScopeKey(departmentId);
      if (!isWildcard && scopedIdSet.size > 0 && !scopedIdSet.has(normalizedDeptId)) return;

      const row = ethicSurveyRecordToRow(docSnap.id, d as Record<string, unknown>);
      seenDocIds.add(docSnap.id);
      rows.push(row);
    });
  };

  if (!scopedDepartmentIds.length || isWildcard) {
    // Full scope or admin: bounded read (rules allow); prefer date filters when provided.
    const snapshot = await getDocs(query(coll, limit(ETHIC_WILDCARD_READ_LIMIT)));
    appendRows(snapshot);
  } else {
    // Limited scope (e.g. HWN, SN): query only by departmentId so we never request
    // documents the user is not allowed to read (avoids permission-denied on other depts).
    const settled = await Promise.allSettled(
      scopedDepartmentIds.flatMap((departmentId) =>
        buildDepartmentIdQueryCandidates(departmentId).map((candidateId) =>
          getDocs(query(coll, where('departmentId', '==', candidateId))),
        ),
      ),
    );

    let permissionDeniedCount = 0;
    let fulfilledQueryCount = 0;
    settled.forEach((result) => {
      if (result.status === 'fulfilled') {
        fulfilledQueryCount += 1;
        appendRows(result.value);
        return;
      }
      if (isPermissionDeniedError(result.reason)) {
        permissionDeniedCount += 1;
        return;
      }
      throw result.reason;
    });

    if (!rows.length && fulfilledQueryCount === 0 && permissionDeniedCount > 0) {
      const diag = JSON.stringify({
        scope: 'ethic',
        scopedDepartmentIds,
        fulfilledQueryCount,
        permissionDeniedCount,
      });
      console.error('[ethicData] permission-denied diagnostic', diag);
      const error = new Error(`Permission denied while reading ethic survey scope (depts=[${scopedDepartmentIds.join(',')}])`);
      (error as { code?: string }).code = 'permission-denied';
      throw error;
    }
  }

  // Client-side filters (timestamp format: "20/2/2026, 18:40:02" or ISO)
  let filtered = rows;
  if (options.startDate || options.endDate) {
    filtered = filtered.filter((row) => {
      const raw = row.timestamp;
      if (!raw) return false;
      const parsed = parseEthicTimestamp(raw);
      if (!parsed) return false;
      if (options.startDate && parsed < options.startDate) return false;
      if (options.endDate && parsed > options.endDate) return false;
      return true;
    });
  }
  if (options.position) {
    filtered = filtered.filter((row) => row.position === options.position);
  }
  if (options.workExperience) {
    filtered = filtered.filter((row) => row.workExperience === options.workExperience);
  }
  return filtered;
}

function parseEthicTimestamp(raw: string): Date | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  // Thai format "20/2/2026, 18:40:02" -> d/m/yyyy, hh:mm:ss
  const thaiMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (thaiMatch) {
    const [, d, m, y] = thaiMatch;
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    return isNaN(date.getTime()) ? null : date;
  }
  const iso = new Date(trimmed);
  return isNaN(iso.getTime()) ? null : iso;
}

export interface EthicQuestionStats {
  questionId: number;
  mean: number;
  variance: number;
  count: number;
  pctBelow35: number;
  pctLow12: number;
}

export interface EthicDomainStats {
  domainId: string;
  mean: number;
  variance: number;
  count: number;
  pctBelow35: number;
}

export interface EthicAggregates {
  hospitalOverall: number;
  hospitalPctBelow35: number;
  perDepartment: Record<
    string,
    {
      overall: number;
      count: number;
      domainMeans: Record<string, number>;
      pctBelow35: number;
    }
  >;
  perQuestion: Record<number, EthicQuestionStats>;
  perDomain: Record<string, EthicDomainStats>;
  totalResponses: number;
}

function variance(arr: number[]): number {
  if (!arr.length) return 0;
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
  return arr.reduce((s, v) => s + (v - mean) ** 2, 0) / arr.length;
}

export function computeEthicAggregates(rows: EthicSurveyRow[]): EthicAggregates {
  const perDept: EthicAggregates['perDepartment'] = {};
  const perQuestion: Record<number, number[]> = {};
  const perDomainByDept: Record<string, Record<string, number[]>> = {};
  const hospitalScores: number[] = [];
  const hospitalBelow35: number[] = [];

  rows.forEach((row) => {
    const answers = Array.isArray(row.answers)
      ? Object.fromEntries(row.answers.map((v, i) => [i + 1, v]))
      : row.answers;
    const score = row.score ?? computeOverallScore(answers as Record<number, number>);
    hospitalScores.push(score);
    hospitalBelow35.push(score < THRESHOLD_ACCEPTABLE ? 1 : 0);

    const deptId = row.departmentId || 'unknown';
    if (!perDept[deptId]) {
      perDept[deptId] = {
        overall: 0,
        count: 0,
        domainMeans: {},
        pctBelow35: 0,
      };
    }
    perDept[deptId].count++;
    perDept[deptId].overall += score;
    perDept[deptId].pctBelow35 += score < THRESHOLD_ACCEPTABLE ? 1 : 0;

    for (let q = 1; q <= 52; q++) {
      const v = (answers as Record<number, number>)[q];
      if (typeof v === 'number' && v >= 1 && v <= 5) {
        if (!perQuestion[q]) perQuestion[q] = [];
        perQuestion[q].push(v);
      }
    }

    ETHIC_DOMAINS.forEach((domain) => {
      const vals: number[] = [];
      for (let q = domain.questions[0]; q <= domain.questions[1]; q++) {
        const v = (answers as Record<number, number>)[q];
        if (typeof v === 'number' && v >= 1 && v <= 5) vals.push(v);
      }
      if (vals.length) {
        if (!perDomainByDept[deptId]) perDomainByDept[deptId] = {};
        if (!perDomainByDept[deptId][domain.id]) perDomainByDept[deptId][domain.id] = [];
        perDomainByDept[deptId][domain.id].push(
          vals.reduce((a, b) => a + b, 0) / vals.length,
        );
      }
    });
  });

  const hospitalOverall =
    hospitalScores.length ? hospitalScores.reduce((a, b) => a + b, 0) / hospitalScores.length : 0;
  const hospitalPctBelow35 =
    hospitalBelow35.length ? hospitalBelow35.reduce((a, b) => a + b, 0) / hospitalBelow35.length : 0;

  Object.keys(perDept).forEach((deptId) => {
    const d = perDept[deptId];
    d.overall = d.count ? d.overall / d.count : 0;
    d.pctBelow35 = d.count ? d.pctBelow35 / d.count : 0;
    const domainMeans = perDomainByDept[deptId] ?? {};
    Object.keys(domainMeans).forEach((domainId) => {
      const arr = domainMeans[domainId];
      d.domainMeans[domainId] =
        arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    });
  });

  const perQuestionStats: Record<number, EthicQuestionStats> = {};
  for (let q = 1; q <= 52; q++) {
    const arr = perQuestion[q] ?? [];
    const mean = arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    const pctBelow35 = arr.length ? arr.filter((v) => v < THRESHOLD_ACCEPTABLE).length / arr.length : 0;
    const pctLow12 = arr.length ? arr.filter((v) => v <= 2).length / arr.length : 0;
    perQuestionStats[q] = {
      questionId: q,
      mean,
      variance: variance(arr),
      count: arr.length,
      pctBelow35,
      pctLow12,
    };
  }

  const perDomain: Record<string, EthicDomainStats> = {};
  ETHIC_DOMAINS.forEach((domain) => {
    const allVals: number[] = [];
    rows.forEach((row) => {
      const answers = Array.isArray(row.answers)
        ? Object.fromEntries(row.answers.map((v, i) => [i + 1, v]))
        : row.answers;
      const vals: number[] = [];
      for (let q = domain.questions[0]; q <= domain.questions[1]; q++) {
        const v = (answers as Record<number, number>)[q];
        if (typeof v === 'number' && v >= 1 && v <= 5) vals.push(v);
      }
      if (vals.length) allVals.push(vals.reduce((a, b) => a + b, 0) / vals.length);
    });
    const mean = allVals.length ? allVals.reduce((a, b) => a + b, 0) / allVals.length : 0;
    perDomain[domain.id] = {
      domainId: domain.id,
      mean,
      variance: variance(allVals),
      count: allVals.length,
      pctBelow35: allVals.length ? allVals.filter((v) => v < THRESHOLD_ACCEPTABLE).length / allVals.length : 0,
    };
  });

  return {
    hospitalOverall,
    hospitalPctBelow35,
    perDepartment: perDept,
    perQuestion: perQuestionStats,
    perDomain,
    totalResponses: rows.length,
  };
}

export function computeDomainCorrelations(rows: EthicSurveyRow[]): Record<string, Record<string, number>> {
  const domainScores: Record<string, number[]> = {};
  ETHIC_DOMAINS.forEach((d) => (domainScores[d.id] = []));

  rows.forEach((row) => {
    const answers = Array.isArray(row.answers)
      ? Object.fromEntries(row.answers.map((v, i) => [i + 1, v]))
      : row.answers;
    ETHIC_DOMAINS.forEach((domain) => {
      const vals: number[] = [];
      for (let q = domain.questions[0]; q <= domain.questions[1]; q++) {
        const v = (answers as Record<number, number>)[q];
        if (typeof v === 'number' && v >= 1 && v <= 5) vals.push(v);
      }
      if (vals.length) domainScores[domain.id].push(vals.reduce((a, b) => a + b, 0) / vals.length);
    });
  });

  const correlation = (a: number[], b: number[]): number => {
    const n = Math.min(a.length, b.length);
    if (n < 2) return 0;
    const meanA = a.reduce((s, v) => s + v, 0) / n;
    const meanB = b.reduce((s, v) => s + v, 0) / n;
    let num = 0,
      denA = 0,
      denB = 0;
    for (let i = 0; i < n; i++) {
      const da = a[i] - meanA;
      const db = b[i] - meanB;
      num += da * db;
      denA += da * da;
      denB += db * db;
    }
    const den = Math.sqrt(denA * denB);
    return den === 0 ? 0 : num / den;
  };

  const out: Record<string, Record<string, number>> = {};
  ETHIC_DOMAINS.forEach((d1) => {
    out[d1.id] = {};
    ETHIC_DOMAINS.forEach((d2) => {
      out[d1.id][d2.id] =
        d1.id === d2.id ? 1 : correlation(domainScores[d1.id] ?? [], domainScores[d2.id] ?? []);
    });
  });
  return out;
}
